import React, { useEffect, useMemo, useState } from "react";
import { useAppFormContext } from "../rhf/FormProvider";
import Accordion from "./Accordion/Accordion";
import { FilterInputs } from "./utils/FilterInputs";
import { FieldPath } from "../rhf/FieldPath";
import {
  buildPocDefaults,
  buildPocRules,
  pocDataToValues,
} from "../utils/addPocControl";
import usePocGroup from "./usePocGroup";

/**
 * One named Points of Contact section (react-hook-form).
 * `required` is the POC "status": all fields required vs. "fill one → fill all".
 */
const CommonControl = ({ keyName, children, pocs, required = false, title = "", arr = [] }) => {
  const form = useAppFormContext();
  const [ready, setReady] = useState(() => form.getValues(keyName) !== undefined);

  const id = useMemo(
    () =>
      title.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") +
      "-content",
    [title],
  );

  // Add this POC's fields and rules to the form
  useEffect(() => {
    if (form.getValues(keyName) === undefined) {
      form.setValue(keyName, buildPocDefaults(keyName, arr));
    }
    form.setRules(buildPocRules(keyName, keyName, required, arr));
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fill in the saved POC data
  useEffect(() => {
    if (!pocs || !pocs.length) return;
    const saved = pocs.find((el) => el.type === keyName);
    if (!saved) return;
    const current = form.getValues(keyName) ?? buildPocDefaults(keyName, arr);
    Object.entries(pocDataToValues(current, saved)).forEach(([key, value]) => {
      if (value !== current[key]) form.setValue(`${keyName}.${key}`, value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pocs]);

  if (!ready) return null;
  return (
    <FieldPath name={keyName}>
      <PocAccordion id={id} path={keyName} role={keyName} status={required} arr={arr} title={title} required={required}>
        {(Array.isArray(arr) ? arr : [])
          .filter((el) => el !== "role")
          .map((el, index) => (
            <FilterInputs status={el} key={index} />
          ))}
        {children}
      </PocAccordion>
    </FieldPath>
  );
};

export const PocAccordion = ({ id, path, role, status, arr, title, required, children }) => {
  const { value, valid, touched } = usePocGroup(path, role, status, arr);
  return (
    <Accordion id={id ?? "content"} title={title} value={value} valid={valid} touched={touched} required={required}>
      {children}
    </Accordion>
  );
};

export default CommonControl;
